import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocale, useTranslations } from "next-intl";
import { formatLocalDateTime } from "@/lib/datetime-local";
import { cn } from "@/lib/utils";
import type { PlayerModule } from "@/services/api/player";

export type OutlineLessonState = "completed" | "in_progress";

type CourseOutlineSidebarProps = {
  tenantSlug: string;
  courseSlug: string;
  courseTitle: string;
  activeLessonId: string;
  modules: PlayerModule[];
  /** LRN-21: the student's state per lesson id (absent = not started). */
  lessonStates?: Record<string, OutlineLessonState>;
};

function lessonLabel(lesson: PlayerModule["lessons"][number]) {
  return lesson.displayTitle ?? lesson.title;
}

export function CourseOutlineSidebar({
  tenantSlug,
  courseSlug,
  courseTitle,
  activeLessonId,
  modules,
  lessonStates = {},
}: CourseOutlineSidebarProps) {
  const t = useTranslations("player");
  const locale = useLocale();
  const [query, setQuery] = useState("");
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const activeRef = useRef<HTMLAnchorElement>(null);

  const allLessons = useMemo(() => modules.flatMap((mod) => mod.lessons), [modules]);
  const totalLessons = allLessons.length;
  const moduleContainingActive = modules.find((mod) =>
    mod.lessons.some((lesson) => lesson.id === activeLessonId),
  )?.id;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return modules.map((mod) => ({
      ...mod,
      visibleLessons: mod.lessons.filter((lesson) => {
        if (!needle) return true;
        return (
          lessonLabel(lesson).toLowerCase().includes(needle) ||
          (lesson.subtitle ?? "").toLowerCase().includes(needle)
        );
      }),
    }));
  }, [modules, query]);

  const searching = query.trim().length > 0;
  const shownCount = filtered.reduce((count, mod) => count + mod.visibleLessons.length, 0);

  function toggleModule(moduleId: string) {
    setOpenModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  }

  return (
    <aside
      data-testid="course-player-sidebar"
      className="flex max-h-[min(80vh,48rem)] flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--surface-2)] lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]"
    >
      <div className="border-b border-[var(--border)] px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
          {t("courseContents")}
        </p>
        <p className="mt-1 truncate text-sm font-semibold text-[var(--text-heading)]">
          {courseTitle}
        </p>

        {totalLessons > 12 && (
          <div className="relative mt-3">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("outlineSearchPlaceholder")}
              aria-label={t("outlineSearchLabel")}
              data-testid="outline-search"
              className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] py-1.5 px-2 text-sm text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus-visible:border-[var(--accent)] focus-visible:ring-2 focus-visible:ring-[var(--accent-ring)]"
            />
          </div>
        )}
        {searching && (
          <p role="status" className="mt-2 text-xs text-[var(--text-muted)]">
            {t("outlineResults", { shown: shownCount, total: totalLessons })}
          </p>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3" aria-label={t("courseContents")}>
        <ul className="space-y-1">
          {filtered.map((mod) => {
            const isOpen = searching
              ? mod.visibleLessons.length > 0
              : (openModules[mod.id] ?? mod.id === moduleContainingActive);
            if (searching && mod.visibleLessons.length === 0) return null;
            const moduleDone = mod.lessons.filter(
              (lesson) => lessonStates[lesson.id] === "completed",
            ).length;

            return (
              <li key={mod.id} data-testid={`player-module-${mod.id}`}>
                <button
                  type="button"
                  onClick={() => toggleModule(mod.id)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-3)]"
                  aria-expanded={isOpen}
                  data-testid={`player-module-toggle-${mod.id}`}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "inline-block shrink-0 text-[var(--text-muted)] transition-transform",
                      isOpen && "rotate-90",
                    )}
                  >
                    ›
                  </span>
                  <span className="min-w-0 flex-1 truncate">{mod.title}</span>
                  {/* LRN-21: "x/y" lessons complete in this module. */}
                  <span
                    className="shrink-0 text-xs font-normal tabular-nums text-[var(--text-muted)]"
                    data-testid={`player-module-progress-${mod.id}`}
                  >
                    <span aria-hidden>
                      {moduleDone}/{mod.lessons.length}
                    </span>
                    <span className="sr-only">
                      {t("moduleProgress", { done: moduleDone, total: mod.lessons.length })}
                    </span>
                  </span>
                </button>

                {isOpen ? (
                  <ul className="ml-3 space-y-0.5 border-l border-[var(--border)] pl-2">
                    {mod.visibleLessons.map((lesson) => {
                      const isActive = lesson.id === activeLessonId;
                      if (lesson.isLocked) {
                        // LRN-17: the lock and its date are announced, not just drawn.
                        // F9 (W3): the date is shown in the student's own time zone.
                        return (
                          <li key={lesson.id}>
                            <span
                              className="flex items-start gap-1.5 rounded-md px-2 py-1.5 text-sm text-[var(--text-secondary)]"
                              data-testid={`player-lesson-locked-${lesson.id}`}
                            >
                              <span aria-hidden className="shrink-0">
                                🔒
                              </span>
                              <span className="min-w-0">
                                {lessonLabel(lesson)}
                                <span className="block text-xs text-[var(--text-muted)]">
                                  {t("lockedNoDate")}
                                  {lesson.unlockAt ? (
                                    <>
                                      {" — "}
                                      <span
                                        data-testid={`player-lesson-available-from-${lesson.id}`}
                                      >
                                        {t("availableFrom", {
                                          date: formatLocalDateTime(lesson.unlockAt, locale),
                                        })}
                                      </span>
                                    </>
                                  ) : null}
                                </span>
                              </span>
                            </span>
                          </li>
                        );
                      }

                      const state = lessonStates[lesson.id];

                      return (
                        <li key={lesson.id}>
                          <Link
                            ref={isActive ? activeRef : undefined}
                            to={`/t/${tenantSlug}/learn/${courseSlug}/lessons/${lesson.id}`}
                            className={cn(
                              "block rounded-md px-2 py-1.5 text-sm",
                              isActive
                                ? "bg-[var(--accent-soft)] font-semibold text-[var(--accent-ink)]"
                                : "text-[var(--text-secondary)] hover:bg-[var(--surface-3)] hover:text-[var(--text-primary)]",
                            )}
                            data-testid={`player-lesson-${lesson.id}`}
                            aria-current={isActive ? "page" : undefined}
                          >
                            <span className="flex items-start gap-1.5">
                              <span
                                aria-hidden
                                className={cn(
                                  "mt-0.5 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[10px] leading-none",
                                  state === "completed"
                                    ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                                    : state === "in_progress"
                                      ? "border-[var(--accent)] bg-[linear-gradient(90deg,var(--accent)_50%,transparent_50%)]"
                                      : "border-[var(--border-strong)]",
                                )}
                                data-testid={`outline-lesson-state-${lesson.id}`}
                                data-state={state ?? "not_started"}
                              >
                                {state === "completed" ? "✓" : null}
                              </span>
                              <span className="min-w-0">{lessonLabel(lesson)}</span>
                              {state ? (
                                <span className="sr-only">
                                  {state === "completed"
                                    ? t("lessonStateCompleted")
                                    : t("lessonStateInProgress")}
                                </span>
                              ) : null}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
